import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLeads, type Lead } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { useUnifiedMetrics, formatCurrency } from '@/hooks/useUnifiedMetrics';
import { useLogWhatsAppAction, useLogCallAction, useDeferLead } from '@/hooks/useLeadsOperations';
import { generatePriorityActions, PriorityAction } from '@/lib/decisionEngine';
import { buildEliteTodayPlan, type EliteAction } from '@/core/decision-engine/eliteEngine';
import { useLockedFocus } from '@/contexts/LockedFocusContext';
import { useHardMode } from '@/contexts/HardModeContext';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import type { WorkspaceInfo } from '@/contexts/WorkspaceScope.context';
import { ActionOutcomeModal, type ActionOutcome } from '@/components/today/ActionOutcomeModal';
import { NextStepModal, type NextStepType } from '@/components/today/NextStepModal';
import { CloseDealModal, type DealType } from '@/components/today/CloseDealModal';
// Removed: DecisionAssistant - can access via Assistant page
import { FollowUpsNeeded } from '@/components/today/FollowUpsNeeded';
import { MandatoryActionCard } from '@/components/today/MandatoryActionCard';
import { ActionRail } from '@/components/today/ActionRail';
import { MomentumIndicator } from '@/components/today/MomentumIndicator';
import { SendToPhoneModal } from '@/components/today/SendToPhoneModal';
import { IndispensabilityEngine } from '@/components/today/IndispensabilityEngine';
import { PriorityBadge } from '@/components/today/PriorityBadge';
// Removed: ExecutionScorecard, AntiSabotageEngine, TomorrowPreview - too much clutter
import { Button } from '@/components/ui/button';
import { ProButton } from '@/components/ui/pro-button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/ui/page-header';
import { SectionCard } from '@/components/ui/section-card';
import { SmartCard } from '@/components/ui/smart-card';
import { KpiValue } from '@/components/ui/kpi-value';
import { InlineMeta } from '@/components/ui/inline-meta';
import { EmptyState } from '@/components/EmptyState';
import { ListSkeleton } from '@/components/ui/skeletons';
import { toast } from 'sonner';
import { errorMessage } from '@/lib/errors';
import { useCompleteAction } from '@/hooks/useActionCompletion';
import { trackActionCompleted } from '@/lib/analytics';
import { useAuth } from '@/lib/auth';
import {
  AlertTriangle,
  Trophy,
  XCircle,
  DollarSign,
  ArrowUpRight,
  Plus,
  Upload,
  TrendingUp,
  Users,
  Activity,
  ArrowRight,
  Target,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { differenceInDays, format } from 'date-fns';
import { getContactLabel, getFollowUpLabel } from '@/lib/timeLabels';
import { computeInaction } from '@/lib/inactionEngine';
import { INACTION_COPY } from '@/lib/inactionCopy';

// Context Metric
function ContextMetric({ 
  label, 
  value, 
  icon: Icon, 
  href,
  tooltip,
}: { 
  label: string; 
  value: string | number; 
  icon: React.ElementType;
  href?: string;
  tooltip?: string;
}) {
  const content = (
    <div 
      className={cn(
        "flex items-center gap-3 p-3 rounded-xl bg-secondary/20 border border-border/40",
        href && "hover:bg-secondary/40 hover:border-border/60 hover:shadow-sm transition-all duration-200 cursor-pointer"
      )}
      title={tooltip}
    >
      <Icon className="w-4 h-4 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold tabular-nums">{value}</p>
        <p className="text-[11px] text-muted-foreground">{label}</p>
      </div>
    </div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }
  return content;
}

// Setup Step — premium card, single block, luxury spacing
function SetupStep({
  title,
  description,
  completed,
  href,
  number,
  isStartHere,
  hintBelow,
}: {
  title: string;
  description: string;
  completed?: boolean;
  href: string;
  number: number;
  isStartHere?: boolean;
  hintBelow?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      {isStartHere && (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-[11px] font-medium rounded-full px-2.5 py-0.5 border border-primary/20 text-primary bg-primary/5">
            Start here
          </Badge>
        </div>
      )}
      <Link to={href} className="block">
        <div
          className={cn(
            'flex items-center gap-5 rounded-2xl border px-6 py-6 transition-all duration-200',
            'border-black/10 dark:border-white/10',
            'shadow-sm',
            completed
              ? 'bg-success/5 border-success/20 opacity-70'
              : 'bg-card hover:border-primary/25 hover:bg-primary/[0.02] hover:-translate-y-[1px] hover:shadow-md dark:hover:shadow-[0_12px_40px_rgba(0,0,0,0.25)]'
          )}
        >
          <div
            className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center text-base font-semibold flex-shrink-0',
              completed ? 'bg-success/10 text-success' : 'bg-primary/10 text-primary'
            )}
          >
            {completed ? <CheckCircle className="w-5 h-5" /> : number}
          </div>
          <div className="flex-1 min-w-0">
            <p className={cn('text-[15px] font-semibold text-foreground', completed && 'line-through text-muted-foreground')}>
              {title}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">{description}</p>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </div>
      </Link>
      {hintBelow && (
        <p className="text-xs text-muted-foreground pl-1">{hintBelow}</p>
      )}
    </div>
  );
}

// TodayContent: Contains all hooks that require workspaceId
// This component is only rendered when activeWorkspaceId exists, ensuring hooks are always called
function TodayContent({ workspaceId }: { workspaceId: string }) {
  const navigate = useNavigate();
  const { data: leads, isLoading, error } = useLeads();
  const { data: deals } = useDeals();
  const { metrics } = useUnifiedMetrics();
  const { isLocked, oneThing } = useLockedFocus();
  const { user } = useAuth();
  const { activeWorkspaceId } = useWorkspaceScope();
  const deferLead = useDeferLead();
  const logWhatsApp = useLogWhatsAppAction();
  const logCall = useLogCallAction();
  const completeAction = useCompleteAction();
  // Optimistic: hide item from Today as soon as user completes (Done). Persisted via lead status/done.
  const [optimisticCompletedIds, setOptimisticCompletedIds] = useState<Set<string>>(new Set());
  // State hooks - MUST be called unconditionally
  const [currentAction, setCurrentAction] = useState<PriorityAction | null>(null);
  const [outcomeModalOpen, setOutcomeModalOpen] = useState(false);
  const [nextStepModalOpen, setNextStepModalOpen] = useState(false);
  const [closeDealModalOpen, setCloseDealModalOpen] = useState(false);
  const [showLoopExample, setShowLoopExample] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState<ActionOutcome | null>(null);
  const [selectedActionType, setSelectedActionType] = useState<'call' | 'whatsapp' | 'email' | 'meeting'>('call');
  const [sendToPhoneAction, setSendToPhoneAction] = useState<{ lead: Lead; dealValue?: number; actionType: 'call' | 'whatsapp' } | null>(null);
  const [sendToPhoneModalOpen, setSendToPhoneModalOpen] = useState(false);

  // Only show in Today leads that are not completed (Done). Persisted: is_completed, status=done, completed_at.
  const isLeadCompleted = (l: Lead) =>
    l.is_completed === true ||
    (l as { status?: string; completed_at?: string | null }).status === 'done' ||
    (l as { completed_at?: string | null }).completed_at != null;

  const [showCompleted, setShowCompleted] = useState(false);

  const todayLeads = useMemo(() => {
    const base = leads ?? [];
    if (showCompleted) {
      return base.filter((l) => !optimisticCompletedIds.has(l.id));
    }
    return base.filter(
      (l) => !isLeadCompleted(l) && !optimisticCompletedIds.has(l.id)
    );
  }, [leads, optimisticCompletedIds, showCompleted]);

  // Elite Decision Engine - Revenue-driven prioritization (only non-completed leads)
  // CRITICAL: Must be defined BEFORE any useEffect that references it to prevent TDZ errors during bundling
  const elitePlan = useMemo(() => {
    // CRITICAL: Allow elitePlan to be built even if deals is empty (leads-only mode)
    if (!todayLeads.length) {
      return {
        oneThing: null,
        mandatory: [],
        optional: [],
        tomorrow: [],
        insights: {
          executionDebt: { debtPoints: 0, debtMoney: 0 },
          dailyCapacity: { mandatoryCount: 0, optionalCount: 0, backlogCount: 0 },
          pipelineHygiene: { missingCloseDates: 0, missingNextSteps: 0 },
          temperatureDrift: { hotToWarm: 0 },
        },
      };
    }

    return buildEliteTodayPlan({
      leads: todayLeads,
      deals: deals || [],
      config: {
        maxColdPerDay: 3,
        staleDealDays: 5,
        mandatoryLimit: 12,
        optionalLimit: 18,
        enableTemperatureDrift: false,
      },
    });
  }, [todayLeads, deals]);

  // Convert EliteActions to PriorityActions for backward compatibility
  const allActions = useMemo(() => {
    const eliteActions = [...elitePlan.mandatory, ...elitePlan.optional];
    return eliteActions.map((eliteAction): PriorityAction & { 
      contact?: any;
      priority?: number;
      priorityReasons?: Array<{ reason: string; weight: number }>;
      hasFutureFollowUp?: boolean;
    } => ({
      id: eliteAction.id,
      leadId: eliteAction.leadId,
      lead: eliteAction.lead,
      score: eliteAction.priorityScore,
      reasons: eliteAction.reasons,
      recommendedAction: eliteAction.contact?.whatsappLink ? 'whatsapp' : 
        eliteAction.contact?.telLink ? 'call' : 'email',
      urgency: eliteAction.priorityScore >= 70 ? 'high' : 
        eliteAction.priorityScore >= 45 ? 'medium' : 'low',
      title: `${eliteAction.actionType.replace(/_/g, ' ')}: ${eliteAction.lead.name}`,
      suggestedMessage: eliteAction.recommendedScript.join(' '),
      revenueAtRisk: eliteAction.moneyAtRisk,
      dealValue: eliteAction.deal?.value || 0,
      contact: eliteAction.contact,
      priority: eliteAction.priority,
      priorityReasons: eliteAction.priorityReasons,
      hasFutureFollowUp: eliteAction.hasFutureFollowUp,
    }));
  }, [elitePlan]);

  // Use elite plan data
  const priorityStack = elitePlan.mandatory.slice(0, 5).map(eliteAction => {
    const action = allActions.find(a => a.id === eliteAction.id);
    return action || allActions[0];
  }).filter(Boolean);
  
  const primaryAction = elitePlan.oneThing 
    ? allActions.find(a => a.id === elitePlan.oneThing!.id) || allActions[0] || null
    : allActions[0] || null;
  
  // Use mandatory actions count as risk indicator
  const riskCount = elitePlan.mandatory.length;

  // Resolved today
  const resolvedToday = useMemo(() => {
    if (!leads) return { won: [], lost: [] };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const won = leads.filter(l => {
      if (l.pipeline_stage !== 'won') return false;
      const updated = new Date(l.updated_at);
      updated.setHours(0, 0, 0, 0);
      return updated.getTime() === today.getTime();
    });
    
    const lost = leads.filter(l => {
      if (l.pipeline_stage !== 'lost') return false;
      const updated = new Date(l.updated_at);
      updated.setHours(0, 0, 0, 0);
      return updated.getTime() === today.getTime();
    });
    
    return { won, lost };
  }, [leads]);

  const getDealForLead = (leadId: string) => {
    // Get the most recent active deal, or any deal if closing
    const deal = deals?.find(d => d.lead_id === leadId);
    return deal ? { id: deal.id, value: deal.value || 0 } : undefined;
  };

  const handleCall = (action: PriorityAction) => {
    // Direct call from PC
    const eliteAction = [...elitePlan.mandatory, ...elitePlan.optional]
      .find(a => a.id === action.id);
    
    if (eliteAction?.contact?.telLink) {
      window.open(eliteAction.contact.telLink, '_blank');
      logCall.mutate(action.leadId);
    } else if (action.lead.phone) {
      window.open(`tel:${action.lead.phone}`, '_blank');
      logCall.mutate(action.leadId);
    }
  };

  const handleWhatsApp = (action: PriorityAction) => {
    // Direct WhatsApp from PC
    const eliteAction = [...elitePlan.mandatory, ...elitePlan.optional]
      .find(a => a.id === action.id);
    
    if (eliteAction?.contact?.whatsappLink) {
      const message = encodeURIComponent(action.suggestedMessage || `Hi ${action.lead.name}`);
      window.open(`${eliteAction.contact.whatsappLink}?text=${message}`, '_blank');
      logWhatsApp.mutate(action.leadId);
    } else if (action.lead.phone) {
      const cleanPhone = action.lead.phone.replace(/\D/g, '');
      const message = encodeURIComponent(action.suggestedMessage || `Hi ${action.lead.name}`);
      window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
      logWhatsApp.mutate(action.leadId);
    }
  };

  const handleSendToPhone = (action: PriorityAction, actionType: 'call' | 'whatsapp') => {
    // Open Send to Phone modal (QR code)
    const dealInfo = getDealForLead(action.leadId);
    setSendToPhoneAction({
      lead: action.lead,
      dealValue: dealInfo?.value,
      actionType,
    });
    setSendToPhoneModalOpen(true);
  };

  const handleCardClick = (action: PriorityAction) => {
    // Open lead details directly instead of showing context panel
    navigate(`/app/leads?id=${action.leadId}`);
  };

  const handleOpenLead = (action: PriorityAction) => {
    navigate(`/app/leads?id=${action.leadId}`);
  };

  const handleMarkDone = (action: PriorityAction) => {
    // Determine action type from contact info
    const hasContact = (action as any).contact;
    const actionType = hasContact?.telLink ? 'call' :
      hasContact?.whatsappLink ? 'whatsapp' :
      action.lead.email ? 'email' : 'call';
    
    setCurrentAction(action);
    setSelectedActionType(actionType);
    setOutcomeModalOpen(true);
  };

  const handleOutcomeSelected = (outcome: ActionOutcome) => {
    setSelectedOutcome(outcome);
    setOutcomeModalOpen(false);

    // If Won/Lost, show CloseDealModal
    if (outcome === 'won' || outcome === 'lost') {
      setCloseDealModalOpen(true);
      return;
    }
    // "Not fit / don't contact" → close as DONE (opt_out), no NextStepModal
    if (outcome === 'not_fit' && currentAction) {
      const leadId = currentAction.leadId;
      setOptimisticCompletedIds((s) => new Set(s).add(leadId));
      completeAction.mutate(
        {
          leadId,
          actionType: selectedActionType,
          outcome: 'not_fit',
          nextStep: { type: 'none' },
        },
        {
          onSuccess: () => {
            setOptimisticCompletedIds((s) => {
              const next = new Set(s);
              next.delete(leadId);
              return next;
            });
            setCurrentAction(null);
            setSelectedOutcome(null);
            setSelectedActionType('call');
            toast.success('Updated');
          },
          onError: (error: any) => {
            setOptimisticCompletedIds((s) => {
              const next = new Set(s);
              next.delete(leadId);
              return next;
            });
            toast.error(errorMessage(error, 'Failed to update'));
          },
        }
      );
      return;
    }
    setNextStepModalOpen(true);
  };

  const handleNextStepConfirmed = (nextStep: {
    type: NextStepType;
    date?: Date;
    stage?: string;
  }) => {
    if (!currentAction || !selectedOutcome) return;

    const leadId = currentAction.leadId;
    setOptimisticCompletedIds((s) => new Set(s).add(leadId));

    // Track analytics if from focus mode
    const isFromFocus = currentAction.id === primaryAction?.id;
    if (isFromFocus && user?.id && activeWorkspaceId) {
      try {
        trackActionCompleted(
          user.id,
          activeWorkspaceId,
          'focus_mark_done',
          selectedOutcome,
          leadId
        );
      } catch (error) {
        console.warn('[Today] Failed to track analytics', error);
      }
    }

    completeAction.mutate(
      {
        leadId,
        actionType: selectedActionType,
        outcome: selectedOutcome,
        nextStep,
      },
      {
        onSuccess: () => {
          setOptimisticCompletedIds((s) => {
            const next = new Set(s);
            next.delete(leadId);
            return next;
          });
          setCurrentAction(null);
          setSelectedOutcome(null);
          setSelectedActionType('call');
          setNextStepModalOpen(false);
          toast.success('Updated');
        },
        onError: (error: any) => {
          setOptimisticCompletedIds((s) => {
            const next = new Set(s);
            next.delete(leadId);
            return next;
          });
          toast.error(errorMessage(error, 'Failed to update'));
        },
      }
    );
  };

  const handleDefer = async (action: PriorityAction) => {
    await deferLead.mutateAsync({ leadId: action.leadId, days: 1 });
    toast.success('Deferred to tomorrow');
  };

  const handleCloseDealConfirmed = (data: {
    deal_value: number;
    deal_type: DealType;
    close_reason?: string;
  }) => {
    if (!currentAction || !selectedOutcome) return;

    const leadId = currentAction.leadId;
    const dealInfo = getDealForLead(leadId);
    setOptimisticCompletedIds((s) => new Set(s).add(leadId));

    // Track analytics if from focus mode
    const isFromFocus = currentAction.id === primaryAction?.id;
    if (isFromFocus && user?.id && activeWorkspaceId) {
      try {
        trackActionCompleted(
          user.id,
          activeWorkspaceId,
          'focus_mark_done',
          selectedOutcome,
          leadId
        );
      } catch (error) {
        console.warn('[Today] Failed to track analytics', error);
      }
    }

    completeAction.mutate(
      {
        leadId,
        actionType: selectedActionType,
        outcome: selectedOutcome,
        nextStep: { type: 'none' },
        closeDealData: {
          dealId: dealInfo?.id,
          deal_value: data.deal_value,
          deal_type: data.deal_type,
          close_reason: data.close_reason,
        },
      },
      {
        onSuccess: () => {
          setOptimisticCompletedIds((s) => {
            const next = new Set(s);
            next.delete(leadId);
            return next;
          });
          setCurrentAction(null);
          setSelectedOutcome(null);
          setSelectedActionType('call');
          setCloseDealModalOpen(false);
          toast.success(selectedOutcome === 'won' ? 'Deal closed as Won' : 'Deal closed as Lost');
        },
        onError: (error: any) => {
          setOptimisticCompletedIds((s) => {
            const next = new Set(s);
            next.delete(leadId);
            return next;
          });
          toast.error(errorMessage(error, 'Failed to close deal'));
        },
      }
    );
  };

  // Setup screen logic: show setup only if NO leads in current scope
  // This is based on real leadCount from the filtered query (workspace-scoped)
  const hasLeads = leads && leads.length > 0;
  const hasActions = allActions.length > 0;
  const hasMandatoryActions = elitePlan.mandatory.length > 0;
  const hasOneThing = elitePlan.oneThing !== null;
  
  // CRITICAL: Always show Focus section if we have leads
  // This ensures the page "guides" the user even with minimal data
  // Show Focus if: has leads (oneThing will exist if leads exist after TASK B fix)
  const shouldShowFocus = hasLeads && hasOneThing;
  // Show Mandatory Actions if: has leads (mandatory will exist if leads exist after TASK B fix)
  const shouldShowMandatoryActions = hasLeads && hasMandatoryActions;
  
  // CRITICAL: DEV LOGGING hook MUST be called BEFORE any early returns
  // This prevents React #310 (hooks mismatch)
  // Move guard conditions INSIDE the hook
  useEffect(() => {
    // Guard: only log in dev mode and when not loading/error
    if (process.env.NODE_ENV !== 'development') return;
    if (isLoading || error) return;
    
    console.log('[Today] Section visibility:', {
      hasLeads,
      leadsCount: leads?.length || 0,
      hasOneThing,
      hasActions,
      hasMandatoryActions,
      mandatoryCount: elitePlan.mandatory.length,
      optionalCount: elitePlan.optional.length,
      shouldShowFocus,
      shouldShowMandatoryActions,
      primaryAction: primaryAction ? { id: primaryAction.id, leadName: primaryAction.lead.name } : null,
    });
  }, [isLoading, error, hasLeads, hasOneThing, hasActions, hasMandatoryActions, shouldShowFocus, shouldShowMandatoryActions, primaryAction, leads?.length, elitePlan.mandatory.length, elitePlan.optional.length]);

  // Loading state - render early but AFTER all hooks
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
    );
  }

  // Error state - render early but AFTER all hooks
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-4">We couldn't load your data.</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  const todayTransition = { duration: 0.22, ease: [0.22, 1, 0.36, 1] as const };
  const staggerDelay = 0.06;

  // Main render
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        visible: { transition: { staggerChildren: staggerDelay, delayChildren: 0.04 } },
        hidden: {},
      }}
      className="container mx-auto px-4 py-6 max-w-4xl relative today-page-bg"
    >
      {/* Action Outcome Modal */}
      {currentAction && (
        <ActionOutcomeModal
          open={outcomeModalOpen}
          onOpenChange={setOutcomeModalOpen}
          leadName={currentAction.lead.name}
          onSelect={handleOutcomeSelected}
        />
      )}

      {/* Next Step Modal */}
      {currentAction && selectedOutcome && (selectedOutcome !== 'won' && selectedOutcome !== 'lost') && (
        <NextStepModal
          open={nextStepModalOpen}
          onOpenChange={setNextStepModalOpen}
          leadName={currentAction.lead.name}
          outcome={selectedOutcome}
          onConfirm={handleNextStepConfirmed}
        />
      )}

      {/* Close Deal Modal */}
      {currentAction && selectedOutcome && (selectedOutcome === 'won' || selectedOutcome === 'lost') && (() => {
        const dealInfo = getDealForLead(currentAction.leadId);
        return (
          <CloseDealModal
            open={closeDealModalOpen}
            onOpenChange={setCloseDealModalOpen}
            leadId={currentAction.leadId}
            leadName={currentAction.lead.name}
            dealId={dealInfo?.id}
            existingDealValue={dealInfo?.value}
            outcome={selectedOutcome}
            onConfirm={handleCloseDealConfirmed}
          />
        );
      })()}

      {/* Send to Phone Modal */}
      {sendToPhoneAction && (
        <SendToPhoneModal
          open={sendToPhoneModalOpen}
          onOpenChange={setSendToPhoneModalOpen}
          lead={sendToPhoneAction.lead}
          dealValue={sendToPhoneAction.dealValue}
          actionType={sendToPhoneAction.actionType}
        />
      )}

      {/* Header - Sales Command Center */}
      <motion.div variants={{ hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: todayTransition } }}>
        <PageHeader
          subtitle={`${format(new Date(), 'EEEE, MMMM d')} • Sales Command Center`}
          title="Tell me what to do today to not lose money"
        />
      </motion.div>
      <motion.div
        variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: todayTransition } }}
        className="mt-3 pl-4 border-l-2 border-[hsl(var(--border-soft))] py-1"
      >
        <p className="text-microcopy text-muted-foreground leading-relaxed">
          <span className="text-foreground/80 font-medium">System note:</span> Decision Sales OS does not ask what you want to do — it tells you what you must do to make money.
        </p>
      </motion.div>

      {/* Empty state - Action-first: Import / Connect as primary CTAs (in-app only) */}
      {!hasLeads && (
        <div className="space-y-8">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold text-foreground">To start, bring leads in.</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Import a CSV or connect Meta so Today can decide what to do.
            </p>
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline w-fit mt-1"
              onClick={() => setShowLoopExample(true)}
            >
              How DSOS works
            </button>
          </div>
          <div className="flex flex-col gap-7">
            <SetupStep
              number={1}
              title="Import your leads"
              description="Upload a CSV or add leads manually"
              href="/app/leads"
              isStartHere
              hintBelow={"Without leads, Today can't decide."}
            />
            <SetupStep
              number={2}
              title="Set your monthly target"
              description="Define your revenue goal"
              href="/app/settings"
            />
            <SetupStep
              number={3}
              title="Connect a lead source"
              description="Connect Meta, webhooks, or other sources"
              href="/app/settings/integrations"
            />
          </div>

          <Dialog open={showLoopExample} onOpenChange={setShowLoopExample}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Your DSOS loop</DialogTitle>
                <DialogDescription>
                  How Decision Sales OS keeps you on track in 20 seconds.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">1</div>
                  <div>
                    <p className="font-medium text-foreground text-sm">Today</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">Shows mandatory actions: what must happen to make money. No optional to-dos.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">2</div>
                  <div>
                    <p className="font-medium text-foreground text-sm">Keep Warm</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">Surfaces leads that went quiet so you re-engage before they go cold.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">3</div>
                  <div>
                    <p className="font-medium text-foreground text-sm">Revenue at Risk</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">Highlights deals and follow-ups you&apos;ll lose if you wait.</p>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Has leads - Show Command Center */}
      {hasLeads && (
        <motion.div
          variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { ...todayTransition, delay: staggerDelay * 2 } } }}
          className="space-y-6 md:space-y-8"
        >
          {/* A. ONE THING - Elite Priority / LOCKED FOCUS MODE */}
          {shouldShowFocus && hasOneThing && primaryAction && (
            <motion.div variants={{ hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: todayTransition } }}>
            <SectionCard
              className={cn(
                "card-shell dsos-card dsos-glow dsos-glow-focus noise-layer relative",
                primaryAction.lead.is_completed && "opacity-60"
              )}
            header={
                <div className="flex items-center gap-2">
                  {isLocked && (
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-primary/80" />
                    </span>
                  )}
                  <Target className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  <h2 className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                    {isLocked ? 'LOCKED FOCUS MODE' : 'One Thing'}
                  </h2>
                </div>
              }
              bodyClassName="p-6 relative"
            >
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
                className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 relative"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-mono-label block mb-1.5">
                    Lead · {primaryAction.leadId.slice(0, 8)}
                  </span>
                  <h3 className={cn(
                    "text-title text-lg mb-1.5 break-words",
                    primaryAction.lead.is_completed && "text-muted-foreground line-through"
                  )}>
                    {primaryAction.lead.name}
                  </h3>
                  {primaryAction.dealValue > 0 && (
                    <p className="mb-3">
                      <KpiValue accent size="lg">{formatCurrency(primaryAction.dealValue)}</KpiValue>
                    </p>
                  )}
                  <p className="text-body mb-3">
                    {primaryAction.reasons[0]}
                  </p>
                  
                  {/* Compact status - time-based only, enriched with inaction state */}
                  {elitePlan.oneThing && (() => {
                    const neverContacted = !primaryAction.lead.last_interaction_at;
                    const contactLabel = getContactLabel({
                      neverContacted,
                      lastInteractionAt: primaryAction.lead.last_interaction_at,
                    });
                    const followUpLabel = getFollowUpLabel({
                      nextFollowUpAt: (primaryAction.lead as any).next_follow_up_at,
                      nextActionAt: primaryAction.lead.next_action_at,
                    });

                    const inaction = computeInaction({
                      lead: {
                        ...(primaryAction.lead as any),
                      },
                    });
                    
                    // Get top 2 priority reasons for micro-label (filter out "999" patterns)
                    const priorityReasons = (primaryAction as any).priorityReasons || [];
                    const topReasons = priorityReasons
                      .slice(0, 2)
                      .map((r: { reason: string }) => r.reason)
                      .filter((r: string) => !r.includes('999') && !r.match(/\b999\b/));
                    
                    return (
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
                        {/* Risk indicators: badge + single timing line (no duplicate overdue) */}
                        <span
                          className={cn(
                            "px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide border border-black/10 dark:border-white/10 inline-flex items-center gap-1",
                            inaction.state === 'FRESH' && "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 dark:bg-emerald-500/5",
                            inaction.state === 'DUE' && "text-amber-700 dark:text-amber-400 bg-amber-500/10 dark:bg-amber-500/5",
                            inaction.state === 'OVERDUE' && "text-red-600 dark:text-red-400 bg-red-500/10 dark:bg-red-500/5",
                            inaction.state === 'STALE' && "text-muted-foreground bg-muted-foreground/10 dark:bg-muted-foreground/5",
                          )}
                          aria-label={`Inaction state: ${inaction.state}`}
                        >
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              inaction.state === 'FRESH' && "bg-emerald-400",
                              inaction.state === 'DUE' && "bg-amber-400",
                              inaction.state === 'OVERDUE' && "bg-red-400",
                              inaction.state === 'STALE' && "bg-muted-foreground/80"
                            )}
                          />
                          {inaction.state !== 'OVERDUE' ? inaction.state : null}
                        </span>
                        {/* Single line: when OVERDUE show only "Overdue by X"; else whyNow + dueIn/followUp */}
                        {inaction.state === 'OVERDUE' && inaction.overdueByLabel ? (
                          <InlineMeta icon={Clock}>
                            {INACTION_COPY.labels.overdueBy(inaction.overdueByLabel)}
                          </InlineMeta>
                        ) : (
                          <>
                            <InlineMeta icon={Clock}>
                              {inaction.hasTimingData ? inaction.whyNow : INACTION_COPY.fallback.noTimingData}
                            </InlineMeta>
                            {inaction.hasTimingData && inaction.dueInLabel ? (
                              <InlineMeta>{INACTION_COPY.labels.dueIn(inaction.dueInLabel)}</InlineMeta>
                            ) : followUpLabel ? (
                              <InlineMeta>{followUpLabel}</InlineMeta>
                            ) : null}
                          </>
                        )}
                        {topReasons.length > 0 && (
                          <span className="text-microcopy text-foreground/60">
                            {topReasons.join(' · ')}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <ActionRail
                  action={primaryAction}
                  onCall={handleCall}
                  onWhatsApp={handleWhatsApp}
                  onMarkDone={handleMarkDone}
                  onDefer={handleDefer}
                  onSendToPhone={handleSendToPhone}
                  primaryCelebration
                  className="flex-shrink-0 w-full sm:w-auto"
                />
                {/* Priority badge - bottom right of card (below buttons) */}
                {elitePlan.oneThing && (primaryAction as any).priority && (primaryAction as any).priorityReasons && (
                  <div className="absolute bottom-4 right-4">
                    <PriorityBadge
                      priority={(primaryAction as any).priority}
                      reasons={(primaryAction as any).priorityReasons}
                      hasFutureFollowUp={(primaryAction as any).hasFutureFollowUp}
                      size="md"
                    />
                  </div>
                )}
              </motion.div>
            </SectionCard>
            </motion.div>
          )}

          {/* B. INDISPENSABILITY ENGINE - Revenue at Risk */}
          {leads && deals && (
            <IndispensabilityEngine leads={leads} deals={deals} />
          )}

          {/* C. FOLLOW-UPS NEEDED - Compact, time-based */}
          {deals && deals.length > 0 && (
            <FollowUpsNeeded leads={leads} deals={deals} />
          )}

          {/* D. PRIORITY ACTIONS - Full width, no context panel */}
          <SectionCard
            className="card-shell dsos-card"
            header={
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">Priority Actions</h3>
                    <p className="text-xs text-muted-foreground">
                      {`${priorityStack.length} of ${allActions.length} mandatory actions. Ordered by DSOS priority.`}
                    </p>
                  </div>
                  {hasLeads && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Switch
                        id="today-show-completed"
                        checked={showCompleted}
                        onCheckedChange={setShowCompleted}
                      />
                      <Label htmlFor="today-show-completed" className="text-xs text-muted-foreground cursor-pointer">
                        Show completed
                      </Label>
                    </div>
                  )}
                </div>
              </div>
            }
            bodyClassName="p-0"
          >
            {shouldShowMandatoryActions ? (
              <div className="space-y-3 px-6 pb-6">
                {priorityStack.map((action, index) => (
                  <motion.div
                    key={action.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.22,
                      delay: index * 0.05,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="rounded-[var(--radius-card)] overflow-hidden"
                  >
                    <MandatoryActionCard
                      action={action}
                      index={index}
                      onCall={handleCall}
                      onWhatsApp={handleWhatsApp}
                      onMarkDone={handleMarkDone}
                      onDefer={handleDefer}
                      onCardClick={handleCardClick}
                      onSendToPhone={handleSendToPhone}
                    />
                  </motion.div>
                ))}
                {allActions.length > 5 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: priorityStack.length * 0.06 }}
                  >
                    <Link 
                      to="/app/leads" 
                      className="flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-primary py-4 transition-colors"
                    >
                      View all {allActions.length} actions
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </motion.div>
                )}
              </div>
            ) : (
              <div className="px-6 pb-6">
                <EmptyState
                  icon={Target}
                  title="No actions for Today"
                  description="Add leads or schedule next steps to populate Today."
                  size="sm"
                />
              </div>
            )}
          </SectionCard>

          {/* D. PIPELINE SNAPSHOT */}
          <div className="mb-8">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Pipeline Snapshot
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ContextMetric
                label="Pipeline Value"
                value={formatCurrency(metrics?.pipelineValue || 0)}
                icon={DollarSign}
                href="/app/pipeline"
              />
              <ContextMetric
                label="Win Rate"
                value={`${metrics?.winRate != null ? Number(metrics.winRate).toFixed(0) : 0}%`}
                icon={Trophy}
                href="/app/dashboard"
              />
              <ContextMetric
                label="Open Leads"
                value={metrics?.openLeads || 0}
                icon={Users}
                href="/app/leads"
              />
              <ContextMetric
                label="Need Action"
                value={metrics?.needAction || 0}
                icon={AlertTriangle}
                href="/app/leads?filter=need_action"
                tooltip="View deals that need action now"
              />
            </div>
          </div>

          {/* E. Resolved Today */}
          {(resolvedToday.won.length > 0 || resolvedToday.lost.length > 0) && (
            <div className="mb-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                Resolved Today
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {resolvedToday.won.map(lead => {
                  const deal = deals?.find(d => d.lead_id === lead.id);
                  return (
                    <div key={lead.id} className="flex items-center gap-3 p-3 rounded-lg bg-success/5 border border-success/20">
                      <Trophy className="w-4 h-4 text-success" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{lead.name}</p>
                        {deal?.value && (
                          <p className="text-xs text-success">{formatCurrency(deal.value)}</p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-[10px] border-success/30 text-success">Won</Badge>
                    </div>
                  );
                })}
                {resolvedToday.lost.map(lead => (
                  <div key={lead.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                    <XCircle className="w-4 h-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{lead.name}</p>
                      {lead.lost_reason && (
                        <p className="text-xs text-muted-foreground">{lead.lost_reason}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-[10px]">Lost</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* F. Quick Actions */}
          <div className="pt-6 border-t border-border/50">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/leads">
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Lead
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/leads">
                  <Activity className="w-4 h-4 mr-1.5" />
                  Log Activity
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/pipeline">
                  <TrendingUp className="w-4 h-4 mr-1.5" />
                  View Pipeline
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/leads">
                  <Upload className="w-4 h-4 mr-1.5" />
                  Import CSV
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

// TodayPage: Wrapper that handles workspace initialization
// Only calls minimal hooks (useWorkspaceScope) to prevent hook order issues
export default function Today() {
  const { activeWorkspaceId, mode, isLoading: workspaceLoading, isSwitchingWorkspace, workspaces, setActiveWorkspace } = useWorkspaceScope();

  // Show loading while workspace is being resolved or switching
  if (workspaceLoading || isSwitchingWorkspace) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
    );
  }

  // CRITICAL: 'all' mode is deprecated - auto-migrate to first workspace
  // If activeWorkspaceId is null, show loading (migration in progress)
  if (!activeWorkspaceId) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
    );
  }

  // Render content only when workspaceId is available
  // This ensures all hooks in TodayContent are called with consistent workspaceId
  return <TodayContent workspaceId={activeWorkspaceId} />;
}

// TodayAllView: Aggregated summary for "All" mode (read-only)
function TodayAllView({ workspaces, onSelectWorkspace }: { workspaces: Array<{ workspace_id: string; workspace_name: string }>; onSelectWorkspace: (workspaceId: string) => void }) {
  const { data: leads } = useLeads();
  const { data: deals } = useDeals();
  const { metrics } = useUnifiedMetrics();

  // Aggregate counts per workspace
  const workspaceStats = useMemo(() => {
    if (!leads || !deals) return [];
    
    return workspaces.map(ws => {
      const wsLeads = leads.filter(l => l.workspace_id === ws.workspace_id);
      const wsDeals = deals.filter(d => d.workspace_id === ws.workspace_id);
      const wsPipelineValue = wsDeals.reduce((sum, d) => sum + (d.value || 0), 0);
      const wsOpenLeads = wsLeads.filter(l => l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost').length;
      
      return {
        workspaceId: ws.workspace_id,
        workspaceName: ws.workspace_name,
        leadsCount: wsLeads.length,
        dealsCount: wsDeals.length,
        pipelineValue: wsPipelineValue,
        openLeads: wsOpenLeads,
      };
    });
  }, [leads, deals, workspaces]);

  const totalLeads = leads?.length || 0;
  const totalDeals = deals?.length || 0;
  const totalPipelineValue = deals?.reduce((sum, d) => sum + (d.value || 0), 0) || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
      className="container mx-auto px-4 py-6 max-w-4xl"
    >
      {/* Header */}
      <div className="mb-10">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          {format(new Date(), 'EEEE, MMMM d')} • All Workspaces Overview
        </p>
        <h1 className="text-3xl font-bold tracking-tight text-foreground mb-4">
          Aggregated View - All Workspaces
        </h1>
        
        <div className="p-4 rounded-xl bg-secondary/20 border border-border/40">
          <p className="text-sm text-muted-foreground italic leading-relaxed">
            "All" mode is read-only. Select a specific workspace to execute actions.
          </p>
        </div>
      </div>

      {/* CTA: Select Workspace */}
      <div className="mb-8 p-6 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/[0.08] to-primary/[0.02]">
        <div className="flex items-center gap-3 mb-3">
          <Target className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Select a Workspace to Execute Actions</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Focus, Mandatory Actions, and execution features require a specific workspace selection.
        </p>
        <div className="flex flex-wrap gap-2">
          {workspaces.map(ws => (
            <Button
              key={ws.workspace_id}
              variant="outline"
              size="sm"
              onClick={() => onSelectWorkspace(ws.workspace_id)}
            >
              {ws.workspace_name}
            </Button>
          ))}
        </div>
      </div>

      {/* Aggregated Summary */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Aggregated Summary
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ContextMetric
            label="Total Pipeline Value"
            value={formatCurrency(totalPipelineValue)}
            icon={DollarSign}
          />
          <ContextMetric
            label="Total Leads"
            value={totalLeads}
            icon={Users}
          />
          <ContextMetric
            label="Total Deals"
            value={totalDeals}
            icon={TrendingUp}
          />
          <ContextMetric
            label="Open Leads"
            value={metrics?.openLeads || 0}
            icon={Activity}
          />
        </div>
      </div>

      {/* Per-Workspace Breakdown */}
      {workspaceStats.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Per-Workspace Breakdown
          </h2>
          <div className="space-y-2">
            {workspaceStats.map(stat => (
              <div
                key={stat.workspaceId}
                className="p-4 rounded-lg border border-border/40 bg-card"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm">{stat.workspaceName}</h3>
                  <Badge variant="outline" className="text-xs">
                    {stat.leadsCount} leads
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <div>
                    <span className="font-medium text-foreground">{stat.openLeads}</span> open
                  </div>
                  <div>
                    <span className="font-medium text-foreground">{stat.dealsCount}</span> deals
                  </div>
                  <div>
                    <span className="font-medium text-foreground">{formatCurrency(stat.pipelineValue)}</span> pipeline
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="pt-6 border-t border-border/50">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/leads">
              <Activity className="w-4 h-4 mr-1.5" />
              View All Leads
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/pipeline">
              <TrendingUp className="w-4 h-4 mr-1.5" />
              View Pipeline
            </Link>
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
